/**
 * Centralized-login kickoff. Brand Center is a **relying app**: it never renders a login
 * form or runs an OIDC client. It navigates the browser directly to the centralized
 * saas-login chooser URL, resolved at runtime from `GET /api/branding/by-hostname` and
 * paired to the caller's own domain — never composed from env. It does NOT call
 * `/api/auth/login-url`, whose no-provider default is a Google-direct OAuth URL.
 *
 * The return URL is **host-relative**: each tenant is served on its own `brand.<tenant>`
 * host and one build serves them all, so the origin is read from `window.location.origin`
 * at call time. That lets saas-login host-derive the tenant — no tenant param is ever sent.
 *
 * When the branding call resolves `loginUrl: null`, or the call itself fails, there is no
 * login destination to navigate to — land on the terminal `/auth/error` page rather than
 * falling back to a hardcoded platform host.
 */
import { authApi } from '@/api/auth';
import { brandingApi } from '@/api/branding';
import { registerSessionExpiredHandler, signOut } from './authStore';
import { stashDeepLink } from './silentRefresh';

const TENANT_CONTEXT_REQUIRED_PATH = '/auth/error?errorCode=TENANT_CONTEXT_REQUIRED';

/**
 * Test seam for the top-level navigation. Spy on `navigation.assign` rather than fighting
 * jsdom's non-configurable `window.location`.
 */
export const navigation = {
  assign(url: string): void {
    if (typeof window !== 'undefined') window.location.assign(url);
  },
};

export function returnUrl(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/auth/return`;
}

/** The path the user was trying to reach, for restoring after the round trip. */
export function currentDeepLink(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname + window.location.search;
}

/**
 * Resolve the centralized saas-login chooser URL for a given return URL, from the
 * branding response paired to this host's domain. `null` (no LOGIN host) and a failed
 * branding call both resolve to the terminal error path — never a hardcoded platform host.
 */
export async function centralizedLoginUrl(returnUrl: string): Promise<string> {
  let loginUrl: string | null;
  try {
    ({ loginUrl } = await brandingApi.byHostname(window.location.hostname));
  } catch {
    return TENANT_CONTEXT_REQUIRED_PATH;
  }
  if (!loginUrl) return TENANT_CONTEXT_REQUIRED_PATH;
  const sep = loginUrl.includes('?') ? '&' : '?';
  return `${loginUrl}${sep}returnUrl=${encodeURIComponent(returnUrl)}`;
}

/**
 * Stash the deep link and navigate the browser directly to centralized login.
 * Resolves once the navigation has been requested (the page is leaving).
 */
export async function startCentralizedLogin(deepLink: string): Promise<void> {
  stashDeepLink(deepLink);
  navigation.assign(await centralizedLoginUrl(returnUrl()));
}

/**
 * Sign out: clear the scoped session server-side, drop the in-memory token, then restart
 * the kickoff. The server call is best-effort — a failure must not leave a live token.
 */
export async function logoutAndRestartLogin(): Promise<void> {
  try {
    await authApi.logout();
  } catch {
    /* best effort — the in-memory session is dropped either way */
  }
  signOut();
  await startCentralizedLogin('/');
}

/**
 * Wire `onSessionExpired` (a failed silent refresh, anywhere in the app) to the kickoff.
 * Called once from `main.tsx`; tests register their own handler instead.
 */
export function installSessionExpiredKickoff(): void {
  registerSessionExpiredHandler(() => {
    void startCentralizedLogin(currentDeepLink());
  });
}
